import { useEffect, useState, useMemo } from "react";

export default function MetricsCatalogTable() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedCell, setExpandedCell] = useState(null);

  useEffect(() => {
    fetch("/iris-catalog-5.3a.json")
      .then(res => res.json())
      .then(setData)
      .catch(err => console.error("Failed to load IRIS data", err));
  }, []);

  const filteredData = useMemo(() => {
    return data.filter(row =>
      Object.values(row).some(value =>
        String(value).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [data, search]);

  const isLongContent = (value) => {
    return String(value).length > 100;
  };

  const truncateContent = (value, length = 100) => {
    const str = String(value);
    return str.length > length ? str.substring(0, length) + "..." : str;
  };

  if (!data.length) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading IRIS metrics…</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
         <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => window.history.back()}
        >
          ← Back
        </button>
        <h1 style={styles.title}>IRIS Catalog of Metrics</h1>
        <p style={styles.subtitle}>v5.3a</p>
      </div>
      <div style={styles.searchWrapper}>
        <input
          style={styles.searchInput}
          placeholder="Search metric code, name, theme…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              {Object.keys(data[0]).map(key => (
                <th key={key} style={styles.headerCell}>
                  {key}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredData.slice(0, 200).map((row, i) => (
              <tr key={i} style={styles.bodyRow}>
                {Object.entries(row).map(([key, val], j) => {
                  const cellId = `${i}-${j}`;
                  const isExpanded = expandedCell === cellId;
                  const isLong = isLongContent(val);

                  return (
                    <td
                      key={j}
                      style={{
                        ...styles.cell,
                        ...(isLong && styles.cellLong),
                        cursor: isLong ? "pointer" : "default",
                      }}
                      onClick={() => isLong && setExpandedCell(isExpanded ? null : cellId)}
                    >
                      {isLong && (
                        <span style={styles.expandIcon}>
                          {isExpanded ? "▼" : "▶"}
                        </span>
                      )}
                      <span>
                        {isExpanded ? val || "—" : truncateContent(val)}
                      </span>
                      {isLong && !isExpanded && (
                        <span style={styles.badge}>expandable</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.footer}>
        <p style={styles.footerText}>
          Showing <strong>{Math.min(200, filteredData.length)}</strong> of{" "}
          <strong>{filteredData.length}</strong> rows
        </p>
      </div>

      {expandedCell && (
        <div
          style={styles.modalOverlay}
          onClick={() => setExpandedCell(null)}
        >
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <button
              style={styles.closeButton}
              onClick={() => setExpandedCell(null)}
            >
              ✕
            </button>
            <h3 style={styles.modalTitle}>Full Content</h3>
            <p style={styles.modalContent}>
              {data[parseInt(expandedCell.split("-")[0])][
                Object.keys(data[0])[parseInt(expandedCell.split("-")[1])]
              ] || "—"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "#ffffff",
    padding: "2rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#1f2937",
  },
  header: {
    marginBottom: "2rem",
    paddingBottom: "1.5rem",
    borderBottom: "2px solid #e5e7eb",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "700",
    margin: "0 0 0.5rem 0",
    color: "#111827",
  },
  subtitle: {
    fontSize: "0.875rem",
    color: "#6b7280",
    margin: "0",
  },
  searchWrapper: {
    marginBottom: "2rem",
  },
  searchInput: {
    width: "100%",
    maxWidth: "500px",
    padding: "0.75rem 1rem",
    fontSize: "0.95rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    background: "#ffffff",
    color: "#1f2937",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    outline: "none",
    boxSizing: "border-box",
  },
  tableWrapper: {
    overflow: "auto",
    maxHeight: "calc(100vh - 300px)",
    borderRadius: "0.75rem",
    border: "1px solid #d1d5db",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.875rem",
  },
  headerRow: {
    position: "sticky",
    top: 0,
    background: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
    zIndex: 10,
  },
  headerCell: {
    padding: "1rem",
    textAlign: "left",
    fontWeight: "600",
    whiteSpace: "nowrap",
    color: "#374151",
    borderRight: "1px solid #e5e7eb",
  },
  bodyRow: {
    borderBottom: "1px solid #e5e7eb",
    transition: "background 0.15s ease",
  },
  cell: {
    padding: "1rem",
    verticalAlign: "top",
    color: "#374151",
    wordBreak: "break-word",
    maxWidth: "800px",
    minWidth: "300px",
    borderRight: "1px solid #f3f4f6",
  },
  cellLong: {
    backgroundColor: "#F0F2F6",
    fontWeight: "500",
    cursor: "pointer",
  },
  expandIcon: {
    display: "inline-block",
    marginRight: "0.5rem",
    color: "#02462E",
    fontSize: "0.75rem",
    fontWeight: "bold",
  },
  badge: {
    display: "inline-block",
    marginLeft: "0.5rem",
    padding: "0.25rem 0.5rem",
    fontSize: "0.7rem",
    background: "#ffffff",
    color: "#000000",
    borderRadius: "0.25rem",
    fontWeight: "600",
  },
  footer: {
    marginTop: "1.5rem",
    paddingTop: "1rem",
    borderTop: "1px solid #e5e7eb",
  },
  footerText: {
    fontSize: "0.85rem",
    color: "#6b7280",
    margin: "0",
  },
  loadingContainer: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  loadingText: {
    color: "#6b7280",
    fontSize: "1rem",
    margin: "0",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#ffffff",
    borderRadius: "0.75rem",
    padding: "2rem",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "80vh",
    overflow: "auto",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    top: "1rem",
    right: "1rem",
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#6b7280",
    padding: "0",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: "#111827",
    marginBottom: "1rem",
    marginTop: "0",
  },
  modalContent: {
    fontSize: "0.95rem",
    color: "#374151",
    lineHeight: "1.6",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
    backButton: {
    background: "#f3f4f6",
    border: "1px solid #d1d5db",
    padding: "0.5rem 1rem",
    borderRadius: "0.5rem",
    fontSize: "0.9rem",
    fontWeight: "500",
    color: "#374151",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};